import type { MapItemType, MapProvider } from "@/base/geo"
import type { TestId } from "@/theme/test-id"

import * as Device from "expo-device"
import React from "react"
import snakecaseKeys from "snakecase-keys"

import { SEGMENT_CLIENT } from "@/analytics/analytics-client"
import { ENVIRONMENT, getTimezoneForUser } from "@/base/environment"
import * as Sentry from "@sentry/react-native"

type JsonValue =
  | JsonList
  | JsonMap
  | boolean
  | number
  | string
  | null
  | undefined
type JsonList = JsonValue[]
interface JsonMap {
  [index: number]: JsonValue
  [key: string]: JsonValue
}

type TrackableEvent =
  | {
      mapItemEntrypoint:
        | "list_item"
        | "map_marker"
        | "map_polygon"
        | "selected_device_sheet"
        | "selected_reel_run_sheet"
      mapItemType: MapItemType | "farm"
      name: "select_map_item"
    }
  | {
      name: "external_link_press"
      pathname: string | null
      service_name: MapProvider
    }
  | { elementName: TestId; name: "element_press" }
  | { help_topic: string; name: "open_help_dialog" }
  | { name: "backend_request"; requestedAction: string }
  | { name: "create_support_ticket"; subject: string }

type TrackEventArg = TrackableEvent & {
  options?: JsonMap
}
const device = {
  brand: Device.brand,
  designName: Device.designName,
  deviceName: Device.deviceName,
  deviceYearClass: Device.deviceYearClass,
  isDevice: Device.isDevice,
  manufacturer: Device.manufacturer,
  modelId:
    typeof Device.modelId === "string" || typeof Device.modelId === "number"
      ? Device.modelId
      : null,
  modelName: Device.modelName,
  osBuildFingerprint: Device.osBuildFingerprint,
  osBuildId: Device.osBuildId,
  osInternalBuildId: Device.osInternalBuildId,
  osName: Device.osName,
  osVersion: Device.osVersion,
  platformApiLevel: Device.platformApiLevel,
  productName: Device.productName,
  supportedCpuArchitectures: Device.supportedCpuArchitectures,
  totalMemory: Device.totalMemory,
}
device satisfies JsonMap

// TODO: Can we combine this with Analytics.tsx?
export function trackEvent(params: TrackEventArg) {
  const {
    appBuildNumber,
    applicationId,
    appVersion,
    isEndToEndTest,
    locale,
    releaseChannel,
    targetDatabaseName,

    updateId,
  } = ENVIRONMENT
  const { languageCode, regionCode } = locale
  const extra: JsonMap = {
    ...params.options,
    appBuildNumber,
    appVersion,
    applicationId,
    backendUrl: ENVIRONMENT.apiUrl,
    device,
    isEndToEndTest,
    languageCode,
    locale: ENVIRONMENT.locale,
    regionCode,
    releaseChannel,
    targetDatabaseName,
    timezoneResolved: getTimezoneForUser(),
    updateId,
  }
  switch (params.name) {
    case "open_help_dialog": {
      extra.topic = params.help_topic
      break
    }
    case "external_link_press": {
      extra.pathname = params.pathname
      break
    }
    case "backend_request": {
      extra.requestedAction = params.requestedAction
      break
    }
    case "element_press": {
      extra.elementName = params.elementName
      break
    }
    case "create_support_ticket": {
      extra.subject = params.subject
      break
    }
    case "select_map_item": {
      extra.mapItemType = params.mapItemType
      extra.mapItemEntrypoint = params.mapItemEntrypoint
      break
    }
  }

  SEGMENT_CLIENT.track(params.name, extra).catch((error) => {
    Sentry.captureException(error, {
      tags: snakecaseKeys({
        analytics_event_name: params.name,
        ...extra,
      }),
    })
  })
}

// TODO: Set active farm id programmatically in context
export function useTrackEvent() {
  // const activeFarmId = useRootSelector(getActiveFarmIdFromState)
  return React.useCallback((params: TrackEventArg) => {
    return trackEvent(params)
  }, [])
}
